import { useState } from 'react'

import { Box, Divider, Text, useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import { PersonAvatar, Select, StatusTag } from '@factorialco/factorial-one-react/dist/experimental'
import {
  Crown,
  Delete,
  LockLocked,
  Person,
  Suitcase,
} from '@factorialco/factorial-one-react/icons/app'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  AtsGetHiringTeamDocument,
  AtsHiringRolesEnum,
  AtsJobPostingSettingsHiringTeamFragment,
  AtsUpdateHiringManagerRoleDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

import UpsellImage from './image.png'

interface Props {
  canManageAtsSettings: boolean
  hiringManagers: AtsJobPostingSettingsHiringTeamFragment[]
  hasHiringManagerRolesFeature: boolean
  removeHiringManager: (id: number) => void
}

interface HiringManagerItemProps {
  hiringManager: AtsJobPostingSettingsHiringTeamFragment
  isCreator: boolean
  removeHiringManager: (id: number) => void
  showDivider: boolean
  hasHiringManagerRolesFeature: boolean
  canManageAtsSettings: boolean
  setUpsellModalOpen: (open: boolean) => void
}

const HiringManagerItem = ({
  hiringManager: hm,
  isCreator,
  removeHiringManager,
  showDivider,
  hasHiringManagerRolesFeature,
  canManageAtsSettings,
  setUpsellModalOpen,
}: HiringManagerItemProps) => {
  const { openDialog } = useConfirmationDialog()
  const [hiringRole, setHiringRole] = useState<AtsHiringRolesEnum>(hm.hiringRole ?? 'editor')
  const [updateHiringManagerRole, { loading: updatingHiringManagerRole }] = useMutation(
    AtsUpdateHiringManagerRoleDocument,
    {
      variables: {
        id: hm.id,
        hiringRole,
      },
      extractorFn: (data) => data.atsMutations.updateAtsHiringManager,
      refetchQueries: [AtsGetHiringTeamDocument],
    }
  )

  const handleUpdateHiringManagerRole = async (value: AtsHiringRolesEnum) => {
    setHiringRole(value)
    const result = await updateHiringManagerRole({
      variables: {
        id: hm.id,
        hiringRole: value,
      },
    })

    const handled = handleResult(result, () => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
  }

  return (
    <>
      <Box
        padding="s16"
        width="full"
        alignItems="center"
        justifyContent="spaceBetween"
        flexDirection="row"
      >
        <Box alignItems="center" flexDirection="row" gap="s16">
          <PersonAvatar
            src={buildUrl(hm.access?.avatar || undefined)}
            size="medium"
            firstName={hm.access?.firstName || ''}
            lastName={hm.access?.lastName || ''}
          />
          <Box flexDirection="column" gap="s4">
            <Text.Normal weight="medium" color="grey1000">
              {hm.access?.firstName} {hm.access?.lastName}
            </Text.Normal>
            <Text.Normal color="grey800">
              {hm.access?.employee?.contractVersions[0]?.jobTitle || ''}
            </Text.Normal>
          </Box>
        </Box>

        <Box alignItems="center" flexDirection="row" gap="s8">
          {isCreator && (
            <StatusTag
              text={i18n.t('ats.job_posting_settings.hiring_team.roles.owner_tag_label')}
              variant="info"
            />
          )}

          <Select
            options={[
              {
                label: i18n.t('ats.job_posting_settings.hiring_team.roles.recruiter.label'),
                description: i18n.t(
                  'ats.job_posting_settings.hiring_team.roles.recruiter.description'
                ),
                value: 'owner',
                icon: Crown,
              },
              {
                label: i18n.t('ats.job_posting_settings.hiring_team.roles.hiring_manager.label'),
                description: i18n.t(
                  'ats.job_posting_settings.hiring_team.roles.hiring_manager.description'
                ),
                value: 'editor',
                icon: Suitcase,
              },
              {
                label: i18n.t('ats.job_posting_settings.hiring_team.roles.reviewer.label'),
                description: i18n.t(
                  'ats.job_posting_settings.hiring_team.roles.reviewer.description'
                ),
                value: 'reviewer',
                icon: hasHiringManagerRolesFeature ? Person : LockLocked,
              },
            ]}
            onChange={
              isCreator
                ? () => {}
                : (value: AtsHiringRolesEnum) => {
                    if (value === 'reviewer' && !hasHiringManagerRolesFeature) {
                      setUpsellModalOpen(true)
                    } else {
                      handleUpdateHiringManagerRole(value)
                    }
                  }
            }
            value={hm.hiringRole}
            disabled={
              updatingHiringManagerRole ||
              (isCreator && !canManageAtsSettings) ||
              !canManageAtsSettings
            }
          />
          {!isCreator && (
            <Button
              variant="secondary"
              onClick={() =>
                openDialog({
                  title: i18n.t('ats.job_posting_settings.hiring_team.delete_modal.title'),
                  description: i18n.t(
                    'ats.job_posting_settings.hiring_team.delete_modal.description'
                  ),
                  onConfirm: () => removeHiringManager(hm.id),
                  confirmLabel: i18n.t('ats.job_posting_settings.hiring_team.delete_modal.confirm'),
                  cancelLabel: i18n.t('common.cancel'),
                })
              }
              icon={Delete}
              label={i18n.t('ats.job_posting_settings.hiring_team.remove_hiring_manager')}
              hideLabel
            />
          )}
        </Box>
      </Box>
      {showDivider && <Divider />}
    </>
  )
}

export const HiringManagerInfo = ({
  hiringManagers,
  removeHiringManager,
  hasHiringManagerRolesFeature,
  canManageAtsSettings,
}: Props) => {
  const [upsellModalOpen, setUpsellModalOpen] = useState(false)

  return (
    <Box
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      borderRadius={{ all: 'abs016' }}
    >
      {hiringManagers.map((hiringManager, index) => (
        <HiringManagerItem
          key={index}
          hiringManager={hiringManager}
          isCreator={index === 0}
          removeHiringManager={removeHiringManager}
          showDivider={index < hiringManagers.length - 1}
          hasHiringManagerRolesFeature={hasHiringManagerRolesFeature}
          canManageAtsSettings={canManageAtsSettings}
          setUpsellModalOpen={setUpsellModalOpen}
        />
      ))}

      {upsellModalOpen && (
        <UpsellingKit.ProductModal
          isOpen
          onClose={() => setUpsellModalOpen(false)}
          moduleRequested={AvailableModules.ats}
          modalTitle={i18n.t('sidebar.recruitment')}
          image={UpsellImage}
          benefits={[
            i18n.t('ats.job_posting_settings.hiring_team.upsell_modal.benefits.first'),
            i18n.t('ats.job_posting_settings.hiring_team.upsell_modal.benefits.second'),
            i18n.t('ats.job_posting_settings.hiring_team.upsell_modal.benefits.third'),
            i18n.t('ats.job_posting_settings.hiring_team.upsell_modal.benefits.fourth'),
          ]}
          title={i18n.t('ats.job_posting_settings.hiring_team.upsell_modal.title')}
          pageSource="ats"
          requestSource="Hiring team - reviewer"
          requestReason={ValidReasons.MissingFeature}
          featureRequested={Features.ats_customized_features}
        />
      )}
    </Box>
  )
}
